import { createContext, useContext, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DataStaleFlags {
  vision360_stale_at: string | null;
  global_analysis_stale_at: string | null;
}

interface DataStaleContextType {
  isVision360Stale: (patientId: string) => boolean;
  isGlobalAnalysisStale: (patientId: string) => boolean;
  markVision360Stale: (patientId: string) => Promise<void>;
  markGlobalAnalysisStale: (patientId: string) => Promise<void>;
  markBothStale: (patientId: string) => Promise<void>;
  clearVision360Stale: (patientId: string) => Promise<void>;
  clearGlobalAnalysisStale: (patientId: string) => Promise<void>;
  clearStale: (patientId: string) => Promise<void>;
  isLoading: (patientId: string) => boolean;
}

const DataStaleContext = createContext<DataStaleContextType | undefined>(undefined);

async function fetchStaleFlags(patientId: string): Promise<DataStaleFlags | null> {
  const { data, error } = await supabase
    .from("patient_data_stale_flags" as any)
    .select("vision360_stale_at, global_analysis_stale_at")
    .eq("patient_id", patientId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching stale flags:", error);
    return null;
  }

  if (!data) {
    return null;
  }

  return {
    vision360_stale_at: (data as any).vision360_stale_at,
    global_analysis_stale_at: (data as any).global_analysis_stale_at,
  };
}

async function upsertStaleFlag(
  patientId: string,
  updates: Partial<DataStaleFlags>
): Promise<void> {
  const { error } = await supabase
    .from("patient_data_stale_flags" as any)
    .upsert({
      patient_id: patientId,
      vision360_stale_at: updates.vision360_stale_at ?? null,
      global_analysis_stale_at: updates.global_analysis_stale_at ?? null,
    } as any, {
      onConflict: "patient_id",
    });

  if (error) {
    console.error("Error upserting stale flag:", error);
    throw error;
  }
}

async function clearVision360StaleFlag(patientId: string): Promise<void> {
  // Buscar flags atuais primeiro para preservar o outro flag
  const current = await fetchStaleFlags(patientId);
  
  // Se não existe registro, não precisa fazer nada
  if (!current) {
    return;
  }
  
  // Atualizar apenas o campo vision360_stale_at, mantendo global_analysis_stale_at
  const { error } = await supabase
    .from("patient_data_stale_flags" as any)
    .update({
      vision360_stale_at: null,
    } as any)
    .eq("patient_id", patientId);

  if (error) {
    console.error("Error clearing vision360 stale flag:", error);
    throw error;
  }
}

async function clearGlobalAnalysisStaleFlag(patientId: string): Promise<void> {
  // Buscar flags atuais primeiro para preservar o outro flag
  const current = await fetchStaleFlags(patientId);
  
  // Se não existe registro, não precisa fazer nada
  if (!current) {
    return;
  }
  
  // Atualizar apenas o campo global_analysis_stale_at, mantendo vision360_stale_at
  const { error } = await supabase
    .from("patient_data_stale_flags" as any)
    .update({
      global_analysis_stale_at: null,
    } as any)
    .eq("patient_id", patientId);

  if (error) {
    console.error("Error clearing global analysis stale flag:", error);
    throw error;
  }
}

async function clearStaleFlags(patientId: string): Promise<void> {
  const { error } = await supabase
    .from("patient_data_stale_flags" as any)
    .update({
      vision360_stale_at: null,
      global_analysis_stale_at: null,
    } as any)
    .eq("patient_id", patientId);

  if (error) {
    console.error("Error clearing stale flags:", error);
    throw error;
  }
}

export function DataStaleProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const markVision360Stale = async (patientId: string) => {
    await upsertStaleFlag(patientId, {
      vision360_stale_at: new Date().toISOString(),
    });
    queryClient.invalidateQueries({ queryKey: ["data-stale-flags", patientId] });
  };

  const markGlobalAnalysisStale = async (patientId: string) => {
    await upsertStaleFlag(patientId, {
      global_analysis_stale_at: new Date().toISOString(),
    });
    queryClient.invalidateQueries({ queryKey: ["data-stale-flags", patientId] });
  };

  const markBothStale = async (patientId: string) => {
    await upsertStaleFlag(patientId, {
      vision360_stale_at: new Date().toISOString(),
      global_analysis_stale_at: new Date().toISOString(),
    });
    queryClient.invalidateQueries({ queryKey: ["data-stale-flags", patientId] });
  };

  const clearVision360Stale = async (patientId: string) => {
    await clearVision360StaleFlag(patientId);
    queryClient.invalidateQueries({ queryKey: ["data-stale-flags", patientId] });
  };

  const clearGlobalAnalysisStale = async (patientId: string) => {
    await clearGlobalAnalysisStaleFlag(patientId);
    queryClient.invalidateQueries({ queryKey: ["data-stale-flags", patientId] });
  };

  const clearStale = async (patientId: string) => {
    await clearStaleFlags(patientId);
    queryClient.invalidateQueries({ queryKey: ["data-stale-flags", patientId] });
  };

  const isVision360Stale = (patientId: string): boolean => {
    const data = queryClient.getQueryData<DataStaleFlags | null>([
      "data-stale-flags",
      patientId,
    ]);
    return data?.vision360_stale_at !== null && data?.vision360_stale_at !== undefined;
  };

  const isGlobalAnalysisStale = (patientId: string): boolean => {
    const data = queryClient.getQueryData<DataStaleFlags | null>([
      "data-stale-flags",
      patientId,
    ]);
    return (
      data?.global_analysis_stale_at !== null &&
      data?.global_analysis_stale_at !== undefined
    );
  };

  const isLoading = (patientId: string): boolean => {
    const queryState = queryClient.getQueryState(["data-stale-flags", patientId]);
    return queryState?.status === "pending";
  };

  return (
    <DataStaleContext.Provider
      value={{
        isVision360Stale,
        isGlobalAnalysisStale,
        markVision360Stale,
        markGlobalAnalysisStale,
        markBothStale,
        clearVision360Stale,
        clearGlobalAnalysisStale,
        clearStale,
        isLoading,
      }}
    >
      {children}
    </DataStaleContext.Provider>
  );
}

// Hook para carregar flags de um paciente específico
export function useStaleFlags(patientId: string) {
  return useQuery({
    queryKey: ["data-stale-flags", patientId],
    queryFn: () => fetchStaleFlags(patientId),
    staleTime: 30 * 1000, // 30 segundos
    refetchOnWindowFocus: true,
  });
}

export function useDataStale() {
  const context = useContext(DataStaleContext);
  if (context === undefined) {
    throw new Error("useDataStale must be used within a DataStaleProvider");
  }
  return context;
}

